import { BoundNumberField, Css, emptyCell, GridCellContent } from "@homebound/beam";
import { ObjectState } from "@homebound/form-state";
import { ReactNode } from "react";
import { Price } from "src/components";
import { NamedFragment } from "src/fragments/commonFragments.generated";
import {
  PlanBasedBidsTableV2_BidContractRevisionFragment,
  PlanBasedBidsTableV2_BidPackageBidderFragment,
  PlanBasedBidsTableV2_BidPlanChildGroupLineFragment,
} from "./PlanBasedBidsTableV2.generated";

// The input type to enter bid costs in the cost-entry column
export type CostInput = {
  id: string | undefined;
  pbbcliId: string | undefined;
  groupLineId: string;
  costCodeId: string;
  totalCostInCents: number | null | undefined;
};

// Everything the trade (bidder × cost-code) columns need to render a cost cell, independent of which table
// (cost-entry or scope) is hosting them.
export type TradeCostColumnsData = {
  activeBidders: PlanBasedBidsTableV2_BidPackageBidderFragment[];
  bidContractRevisions: PlanBasedBidsTableV2_BidContractRevisionFragment[];
  costCodes: readonly NamedFragment[];
  mode: "view" | "edit";
  // In edit mode, looks up the form-state row backing a given (line, cost code) cell.
  costRowLookup: (bpglId: string, costCodeId: string) => ObjectState<CostInput> | undefined;
};

// One cost column per (bidder × cost code). `isFirstCostCodeForTrade` drives the stacked header so only the
// first cost-code column of each bidder shows the bidder name.
export type TradeColumnDescriptor = {
  bidder: PlanBasedBidsTableV2_BidPackageBidderFragment;
  costCode: NamedFragment;
  isFirstCostCodeForTrade: boolean;
};

// Expand the active bidders × cost codes into one descriptor per cost column, in render order.
export function tradeColumnDescriptors(ctx: TradeCostColumnsData): TradeColumnDescriptor[] {
  return ctx.activeBidders.flatMap((bidder) =>
    ctx.costCodes.map((costCode, idx) => ({ bidder, costCode, isFirstCostCodeForTrade: idx === 0 })),
  );
}

// The stacked header for a cost column: the bidder name above the cost-code label. Only the first cost-code
// column of each bidder shows the name; the rest render an invisible placeholder so the labels stay aligned.
export function tradeColumnHeader(d: TradeColumnDescriptor): GridCellContent {
  const { bidder, costCode, isFirstCostCodeForTrade } = d;
  return {
    content: (
      <div css={Css.df.fdc.w100.$}>
        <span css={Css.smSb.gray900.lineClamp1.if(!isFirstCostCodeForTrade).vh.$}>
          {isFirstCostCodeForTrade ? bidder.name : "."}
        </span>
        <span css={Css.xsSb.gray700.$}>{costCode.name}</span>
      </div>
    ),
    alignment: "left",
  };
}

// The cost cell for a given child group line in a given (bidder × cost code) column. In edit mode it renders a
// BoundNumberField bound to the form-state row; in view mode it shows the matching PB-BCLI's cost (matched by the
// bidder's BCR + cost code), so an unsubmitted request renders empty.
export function tradeCostCell(
  line: PlanBasedBidsTableV2_BidPlanChildGroupLineFragment,
  d: TradeColumnDescriptor,
  ctx: TradeCostColumnsData,
  isNoCost: boolean,
): ReactNode | GridCellContent {
  const { bidder, costCode } = d;
  if (ctx.mode === "edit") {
    const row = ctx.costRowLookup(line.id, costCode.id);
    if (!row) return emptyCell;
    return {
      content: (
        <BoundNumberField
          field={row.totalCostInCents}
          type="cents"
          disabled={
            isNoCost
              ? "This group is marked as no cost. To change this, please edit the bid package groups in step 2 of the edit flow."
              : undefined
          }
        />
      ),
      alignment: "left",
    };
  }
  if (isNoCost) return { content: "No Cost", alignment: "left" };
  const bcr = ctx.bidContractRevisions.find((b) => b.bidContract.id === bidder.bidContract?.id);
  if (!bcr) return emptyCell;
  const pbbcli = line.planBasedBidContractLineItems.find(
    (p) => p.revision.id === bcr.id && p.costCode.id === costCode.id,
  );
  // $0 missing cost placeholders (userSet=false) render as unentered
  return { content: <Price valueInCents={pbbcli?.userSet ? pbbcli.totalCostInCents : undefined} />, alignment: "left" };
}
